import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Package, Truck, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGRNs, useGRNStats } from '@/hooks/finance/useGRN';
import { GRNStatus } from '@/types/database/grn';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';

const statusConfig: Record<GRNStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  draft: { label: 'Draft', variant: 'secondary', icon: <Clock className="h-3 w-3" /> },
  received: { label: 'Received', variant: 'outline', icon: <Package className="h-3 w-3" /> },
  verified: { label: 'Verified', variant: 'default', icon: <CheckCircle className="h-3 w-3" /> },
  closed: { label: 'Closed', variant: 'default', icon: <CheckCircle className="h-3 w-3" /> },
};

const GRNPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<string>('all');

  const statusFilter = activeTab === 'all' ? undefined : activeTab as GRNStatus;
  const { data: grns, isLoading } = useGRNs({ status: statusFilter });
  const { data: stats } = useGRNStats();

  const filteredGRNs = grns?.filter(grn =>
    grn.grn_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    grn.procurement?.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    grn.supplier?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    grn.agent?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Goods Received Notes</h1>
            <p className="text-muted-foreground">Track incoming goods and deliveries</p>
          </div>
          <Button onClick={() => navigate('/finance/accounting/grn/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New GRN
          </Button>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total GRNs</p>
                  <p className="text-2xl font-bold">{stats?.total || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/10 rounded-lg">
                  <Clock className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Draft</p>
                  <p className="text-2xl font-bold">{stats?.draft || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Truck className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Received</p>
                  <p className="text-2xl font-bold">{stats?.received || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending Verification</p>
                  <p className="text-2xl font-bold">{stats?.pending_verification || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by GRN number, product, or supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="draft">Draft</TabsTrigger>
            <TabsTrigger value="received">Received</TabsTrigger>
            <TabsTrigger value="verified">Verified</TabsTrigger>
            <TabsTrigger value="closed">Closed</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <Skeleton className="h-6 w-1/3 mb-2" />
                      <Skeleton className="h-4 w-1/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredGRNs?.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No GRNs found</h3>
                  <p className="text-muted-foreground mb-4">
                    Create a new GRN when goods are received
                  </p>
                  <Button onClick={() => navigate('/finance/accounting/grn/new')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create GRN
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredGRNs?.map((grn) => (
                  <Card 
                    key={grn.id} 
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/finance/accounting/grn/${grn.id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-muted-foreground">
                              {grn.grn_number}
                            </span>
                            <Badge variant={statusConfig[grn.status].variant}>
                              {statusConfig[grn.status].icon}
                              <span className="ml-1">{statusConfig[grn.status].label}</span>
                            </Badge>
                            {grn.verification_status === 'failed' && (
                              <Badge variant="destructive">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Verification Failed
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-medium">
                            {grn.procurement?.product_name || 'General Goods'}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {grn.supplier && <span>Supplier: {grn.supplier.name}</span>}
                            {grn.agent && <span>Agent: {grn.agent.name}</span>}
                            <span>• Delivered: {format(new Date(grn.delivery_date), 'MMM d, yyyy')}</span>
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          {grn.warehouse_location && (
                            <p>Location: {grn.warehouse_location}</p>
                          )}
                          {grn.vehicle_number && (
                            <p>Vehicle: {grn.vehicle_number}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default GRNPage;
